import styled from '@emotion/styled';
import { FC, InputHTMLAttributes, useRef } from 'react';

const StyledInput = styled.input`
  outline: none;
  border: 1px solid #d4d4d4;
`;

interface EditCellProps extends InputHTMLAttributes<HTMLInputElement> {
  onValueChange: (value: string | number) => void;
}

/**
 * An editable table cell component with validation
 * Features:
 * - Handles both text and number inputs
 * - Validates changes before calling onValueChange
 * - Prevents event bubbling when clicked
 */
export const EditCell: FC<EditCellProps> = ({
  value,
  className,
  type,
  onValueChange,
  ...rest
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  /**
   * Handles input blur events with validation
   * - For numbers: validates numeric value and checks for changes
   * - For text: checks for non-empty changes
   * - Resets to original value if validation fails
   */
  const handleInputBlur = () => {
    if (inputRef.current) {
      const newValue = inputRef.current?.value.trim();
      if (type === 'number' && newValue) {
        const numberValue = Number(newValue);
        if (!isNaN(numberValue) && numberValue !== value) {
          onValueChange(numberValue);
        } else {
          inputRef.current.value = String(value);
        }
      } else if (
        type === 'text' &&
        newValue !== undefined &&
        newValue !== value
      ) {
        onValueChange(newValue);
      } else {
        // Reset to original value if invalid or unchanged
        inputRef.current.value = String(value ?? '');
      }
    }
  };

  return (
    <StyledInput
      className={`w-full inline-flex focus:border-[#f97316] outline-none rounded ${className}`}
      ref={inputRef}
      defaultValue={value ?? ''}
      onBlur={handleInputBlur}
      type={type}
      onClick={(e) => e.stopPropagation()}
      {...rest}
    />
  );
};
